import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { CalendarDays, Clock, CheckCircle2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { clinicData } from "../mock";
import Header from "./Header";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, addMonths } from "date-fns";

const minDateObj = new Date();
const maxDateObj = addMonths(minDateObj, 3); // 3 months from today

const services = clinicData.services.map((s) => s.title);

function parseTimeToMinutes(timeStr) {
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  return hours * 60 + minutes;
}

export default function AppointmentBookingPage() {
  const location = useLocation();
  const state = location.state || {};
  const [branch, setBranch] = useState("");
  const [bookingId, setBookingId] = useState(""); // <-- store booking ID

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    consultationType: "Walk-in",
    branch: "",
    date: "",
    time: "",
    message: "",
    address: "",
    pincode: "",
    locality: "",
    flatNo: "",
    landmark: "",
    city: "Visakhapatnam",
    state: "Andhra Pradesh",
    country: "India",
  });

  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get("type");

  React.useEffect(() => {
    if (typeParam) {
      setForm((prev) => ({
        ...prev,
        consultationType:
          typeParam === "home-visit"
            ? "Home Visit"
            : typeParam === "virtual"
            ? "Virtual Consultation"
            : "Walk-in",
      }));
    }
  }, [typeParam]);

  function serviceMapping(text) {
    switch (text) {
      case "Sports Recovery Session":
        return "Sports Injury Recovery";
      case "Pain Relief Session":
        return "Pain Relief & Management";
      case "Chiropractic Session":
        return "Chiropractic Care";
      case "Rehabilitation Session":
        return "Rehabilitation";
      case "Wellness Program":
        return "Customized Wellness Programs";
      case "Corporate Wellness":
        return "Corporate Wellness";
      default:
        return "";
    }
  }

  useEffect(() => {
    if (location.state?.selectedService) {
      const mappedService =
        serviceMapping(location.state.selectedService) ||
        location.state.selectedService;
      setForm((prev) => ({ ...prev, service: mappedService }));
    }
  }, [location.state]);
  React.useEffect(() => {
    if (state.branch) {
      setForm((prev) => ({ ...prev, branch: state.branch }));
    }
  }, [state.branch]);

  const slots = useMemo(
    () => [
      "09:30 AM",
      "10:00 AM",
      "10:30 AM",
      "11:00 AM",
      "11:30 AM",
      "12:00 PM",
      "12:30 PM",
      "01:00 PM",
      "04:30 PM",
      "05:00 PM",
      "05:30 PM",
      "06:00 PM",
      "06:30 PM",
      "07:00 PM",
      "07:30 PM",
      "08:00 PM",
      "08:30 PM",
      "09:00 PM",
    ],
    []
  );

  const today = new Date();
  const selectedDate = form.date ? new Date(form.date) : null;

  const availableSlots = useMemo(() => {
    if (!selectedDate) return slots;

    const isToday = selectedDate.toDateString() === today.toDateString();
    if (!isToday) return slots;

    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    return slots.filter((t) => parseTimeToMinutes(t) > nowMinutes);
  }, [slots, form.date]);

  const [submitting, setSubmitting] = useState(false);
const [submitted, setSubmitted] = useState(false);
const [errors, setErrors] = useState({});
const [emailSent, setEmailSent] = useState(false);
const [error, setError] = useState("");  // <-- ADD THIS


  const resetForm = () => {
  setForm({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    consultationType: "Walk-in",
    branch: "",
    date: "",
    time: "",
    message: "",
    address: "",
    pincode: "",
    locality: "",
    flatNo: "",
    landmark: "",
    city: "Visakhapatnam",
    state: "Andhra Pradesh",
    country: "India",
  });
  
  setSubmitted(false);
  setError("");  // <-- FIX HERE
  setBookingId("");
};


  const onSubmit = async (e) => {
  e.preventDefault();
  setErrors({}); // reset previous field errors
  setEmailSent(false);

  // ----------------------
  // Basic validations
  // ----------------------
  let phoneValue = form.phone.trim();
  if (!phoneValue.startsWith("+91")) {
    phoneValue = "+91" + phoneValue;
  }

  const newErrors = {};

  // Validate first name
  if (!form.firstName.trim()) {
    newErrors.firstName = "Please enter your first name.";
  } else if (!/^[A-Za-z\s]+$/.test(form.firstName)) {
    newErrors.firstName = "First name must contain only letters and spaces.";
  }

  // Validate last name
  if (!form.lastName.trim()) {
    newErrors.lastName = "Please enter your last name.";
  } else if (!/^[A-Za-z\s]+$/.test(form.lastName)) {
    newErrors.lastName = "Last name must contain only letters and spaces.";
  }

  // Validate email
  if (!form.email.trim()) {
  newErrors.email = "Please enter your email address.";
} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
  newErrors.email = "Please enter a valid email address with @.";
}



  // Validate phone
  if (!form.phone.trim()) {
    newErrors.phone = "Please enter your phone number.";
  } else if (!/^\d{10}$/.test(form.phone)) {
    newErrors.phone = "Mobile number must be 10 digits.";
  }


  if (!form.service.trim()) {
  newErrors.service = "Please select a service.";
}

  if (form.consultationType === "Walk-in" && !form.branch.trim()) {
  newErrors.branch = "Please select a branch.";
}
  // Validate consultation type / branch
  if (!form.consultationType && !form.branch) {
    newErrors.consultationType =
      "Please select either a consultation type or a branch.";
  }

  // Validate date and time
  if (!form.date) newErrors.date = "Please select a date.";
  if (!form.time) newErrors.time = "Please select a time slot.";

  // ----------------------
  // Home Visit validation
  // ----------------------
  let address = "";
  if (form.consultationType === "Home Visit") {
    if (!form.pincode?.trim()) newErrors.pincode = "Please enter your pincode.";
    if (!form.locality?.trim())
      newErrors.locality = "Please enter your locality/area.";

    // Combine fields into a full address
    address = `${form.flatNo ? form.flatNo + ", " : ""}${form.locality}, ${
      form.city
    }, ${form.state} - ${form.pincode}, ${form.country}`;
  }

  // If any errors, stop submission
  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  setSubmitting(true);


    try {
      // ----------------------
      // Call backend API
      // ----------------------
      const res = await axios.post(
        "https://vpjc-api-805872224468.asia-south1.run.app/api/appointment/booking-ack",
        {
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          phone: phoneValue,
          service: form.service,
          consultationType: form.consultationType,
          date: form.date,
          time: form.time,

          // 🔹 only send branch if Walk-in
          branch: form.consultationType === "Walk-in" ? form.branch : undefined,

          // Home Visit fields
          pincode:
            form.consultationType === "Home Visit" ? form.pincode : undefined,
          city: form.consultationType === "Home Visit" ? form.city : undefined,
          state:
            form.consultationType === "Home Visit" ? form.state : undefined,
          country:
            form.consultationType === "Home Visit" ? form.country : undefined,
          locality:
            form.consultationType === "Home Visit" ? form.locality : undefined,
          flatNo:
            form.consultationType === "Home Visit" ? form.flatNo : undefined,
          landmark:
            form.consultationType === "Home Visit" ? form.landmark : undefined,
          address: form.consultationType === "Home Visit" ? address : undefined,
        }
      );

      setBookingId(res.data.bookingId);
      setEmailSent(true);
      setSubmitted(true);
    } catch (err) {
      console.error(
        "Error saving appointment:",
        err.response?.data || err.message
      );
      setError(
        "Failed to submit appointment. " +
          (err.response?.data?.detail || err.message || "")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-white relative overflow-hidden">
      <Header hideBookButton={true} />
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/6129237/pexels-photo-6129237.jpeg')",
          opacity: 0.15,
        }}
      ></div>
      <div className="relative w-full max-w-4xl bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left Info Panel */}
          <div className="bg-[#204080] hidden md:flex flex-col p-10 text-white space-y-6">
            <h2 className="text-3xl font-bold mb-4">Plan Your Appointment</h2>
            <p className="mb-6">
              Choose a date and time that fits your schedule. We’ll confirm your
              booking quickly.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <CalendarDays /> Flexible scheduling
              </li>
              <li className="flex items-center gap-2">
                <Clock /> Multiple time slots
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 /> Instant confirmation
              </li>
            </ul>
            {/* Clinic Hours and info omitted for brevity; keep your original code here */}
            {/* Clinic Hours */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3 text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Clinic Hours
              </h3>

              <div className="bg-white/10 rounded-xl p-4 space-y-4">
                {/* Mon - Sat */}
                <div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Mon – Sat</span>
                  </div>
                  <div className="ml-4 mt-1 space-y-1 text-sm">
                    <p>09:00 AM – 09:00 PM</p>
                  </div>
                </div>

                {/* Sunday */}
                <div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Sunday</span>
                  </div>
                  <div className="ml-4 mt-1 text-sm">
                    <p>09:00 AM – 05:00 PM</p>
                  </div>
                </div>
              </div>

              <p className="mt-3 text-sm text-blue-100 italic">
                Prior booking is advised for guaranteed time slot availability.
              </p>
            </div>

            {/* Extra Info */}
            <div className="space-y-2 text-white text-base">
              <p>
                <strong>Average Session:</strong> 45-60 minutes
              </p>
              {form.branch && (
                <p>
                  <strong>Reminder:</strong> Please arrive 10 minutes early
                </p>
              )}
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="p-8 ">
            {submitted ? (
              <div className="text-center">
                <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-green-600" />
                <h3 className="text-2xl font-semibold text-gray-900">
                  Appointment requested!
                </h3>
                <p className="mt-2 text-gray-700">
                  You’ll receive a confirmation shortly at your registered email
                  address.
                </p>
                {bookingId && (
                  <p className="mt-2 text-gray-700">
                    Your Booking ID: <strong>{bookingId}</strong>
                  </p>
                )}
                <button
                  onClick={resetForm}
                  className="mt-6 bg-[#204080] text-white px-5 py-3 rounded-lg"
                >
                  Book another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                {/* All your original inputs, selects, conditional Home Visit fields, branch, date, time, message */}
                {/* Keep everything exactly as in your current code */}
                {error && (
                  <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div>
  <label className="block font-medium mb-1 text-[#204080] text-sm">
    First Name <span className="text-red-600">*</span>
  </label>
  <input
    type="text"
    value={form.firstName}
    onChange={(e) => {
      // Remove non-letter characters
      let value = e.target.value.replace(/[^A-Za-z\s]/g, "");

      // Capitalize first letter of each word
      value = value.replace(/\b\w/g, (char) => char.toUpperCase());

      // Update form AND clear firstName error
      setForm({ ...form, firstName: value });
      setErrors({ ...errors, firstName: "" }); // ✅ clear the error
    }}
    className="w-full p-3 border rounded-lg"
  />
  {errors.firstName && (
    <p className="mt-1 text-red-600 text-sm">{errors.firstName}</p>
  )}
</div>



                <div>
  <label className="block font-medium mb-1 text-[#204080] text-sm">
    Last Name <span className="text-red-600">*</span>
  </label>
  <input
    type="text"
    value={form.lastName}
    onChange={(e) => {
      // Remove non-letter characters
      let value = e.target.value.replace(/[^A-Za-z\s]/g, "");

      // Capitalize first letter of each word
      value = value.replace(/\b\w/g, (char) => char.toUpperCase());

      setForm({ ...form, lastName: value });
      setErrors({ ...errors, lastName: "" }); 
    }}
    className="w-full p-3 border rounded-lg"
  />
  {errors.lastName && (
    <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
  )}
</div>


                <div>
  <label className="block font-medium mb-1 text-[#204080] text-sm">
    Email <span className="text-red-600">*</span>
  </label>
  <input
    type="email"
    value={form.email}
    onChange={(e) => setForm({ ...form, email: e.target.value })}
    onBlur={() => {
      const value = form.email.trim();
      if (!value) {
        setErrors((prev) => ({ ...prev, email: "Please enter your email address." }));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address with @." }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" })); // valid, clear error
      }
    }}
    className="w-full p-3 border rounded-lg"
  />
  {errors.email && (
    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
  )}



                </div>

                <div>
                  <label className="block font-medium mb-1 text-[#204080] text-sm">
                    Phone <span className="text-red-600">*</span>
                  </label>
                  <div className="flex">
                    <span className="px-3 py-2  border border-r-0 rounded-l">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                        if (value.length <= 10)
                          setForm({ ...form, phone: value });
                          setErrors({ ...errors, phone: "" }); // ✅ clear the error
                      }}
                      placeholder="Enter 10-digit mobile number"
                      className="flex-1 border rounded-r-lg p-3"
                    />
                  </div>
                  {errors.phone && (
  <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
)}
                </div>

                <div>
                  <label className="block font-medium mb-1 text-[#204080] text-sm">
                    Select Service <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={(e) => {
  setForm({ ...form, service: e.target.value });
  setErrors({ ...errors, service: "" }); // clear error as user types
}}

                    className={`w-full border rounded-lg px-4 py-3 bg-white text-black text-base focus:outline-none focus:ring-2 focus:ring-[#204080]`}
                  >
                    <option value="">Select a service</option>
                    <option value="Rehabilitation">Rehabilitation</option>
                    {services
                      .filter(
                        (s) =>
                          ![
                            "Sports Injury",
                            "Pain Management",
                            "Rehabilitation",
                            "Wellness Program",
                          ].includes(s)
                      )
                      .map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                  </select>
                  {errors.service && (
  <p className="text-red-600 text-sm mt-1">{errors.service}</p>
)}

                </div>

                {/* Type of Consultation */}
                {/* Type of Consultation */}
                <div>
                  <label className="block font-medium mb-1 text-[#204080] text-sm">
                    Type of Consultation <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="consultationType"
                    value={form.consultationType}
                    onChange={(e) => {
                      const selected = e.target.value;
                      setForm({
                        ...form,
                        consultationType: selected,
                        // Clear branch if hiding branch (Home Visit or Virtual Consultation)
                        branch:
                          selected === "Home Visit" ||
                          selected === "Virtual Consultation"
                            ? ""
                            : form.branch,
                      });
                    }}
                    className="w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#204080] bg-white text-black"
                  >
                    <option value="Walk-in">Walk-in</option>
                    <option value="Home Visit">Home Visit</option>
                    <option value="Virtual Consultation">
                      Virtual Consultation
                    </option>
                    {errors.consultationType && (
  <p className="text-red-600 text-sm mt-1">{errors.consultationType}</p>
)}


                  </select>

                  {/* Show Doctor availability messages based on type */}
                  {/* Home Visit Section */}
                  {/* Home Visit Section */}
                  {form.consultationType === "Home Visit" && (
                    <div className="space-y-3 mt-4">
                      {/* Info message */}
                      <p className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-2">
                        Request will be confirmed based on Doctors availability
                        at Visakhapatnam.
                      </p>

                      {/* Pincode + City */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-medium mb-1 text-[#204080] text-sm">
                            Pincode <span className="text-red-600">*</span>
                          </label>
                          <input
  type="text"
  value={form.pincode}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length <= 6) {
      setForm({ ...form, pincode: value });
      setErrors({ ...errors, pincode: "" }); // clear error as user types
    }
  }}
  placeholder="Enter Pincode"
  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
/>
{errors.pincode && (
  <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>
)}

                        </div>

                        <div>
                          <label className="block font-medium mb-1 text-[#204080] text-sm">
                            City
                          </label>
                          <input
                            type="text"
                            value={form.city || "Visakhapatnam"}
                            readOnly
                            className="w-full p-3 border rounded-lg bg-gray-100 text-gray-700"
                          />
                        </div>
                      </div>

                      {/* State + Country */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-medium mb-1 text-[#204080] text-sm">
                            State
                          </label>
                          <input
                            type="text"
                            value={form.state || "Andhra Pradesh"}
                            readOnly
                            className="w-full p-3 border rounded-lg bg-gray-100 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block font-medium mb-1 text-[#204080] text-sm">
                            Country
                          </label>
                          <input
                            type="text"
                            value={form.country || "India"}
                            readOnly
                            className="w-full p-3 border rounded-lg bg-gray-100 text-gray-700"
                          />
                        </div>
                      </div>

                      {/* Locality / Area / Street */}
                      {/* Locality / Area / Street */}
<div>
  <label className="block font-medium mb-1 text-[#204080] text-sm">
    Locality / Area / Street <span className="text-red-600">*</span>
  </label>
  <textarea
    rows="2"
    value={form.locality}
    onChange={(e) => {
  setForm({ ...form, locality: e.target.value });
  setErrors({ ...errors, locality: "" }); // clear error
}}

    placeholder="Enter Locality / Area / Street"
    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
  ></textarea>
  {errors.locality && (
    <p className="mt-1 text-sm text-red-600">{errors.locality}</p>
  )}
</div>

{/* Landmark (optional) */}
<div>
  <label className="block font-medium mb-1 text-[#204080] text-sm">
    Landmark (optional)
  </label>
  <input
    type="text"
    value={form.landmark}
    onChange={(e) => setForm({ ...form, landmark: e.target.value })}
    placeholder="Nearby Landmark"
    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
  />
  {/* No error message here, since optional */}


                      </div>
                    </div>
                  )}

                  {form.consultationType === "Virtual Consultation" && (
                    <p className="mt-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-2">
                      Request will be confirmed based on Doctors availability.
                    </p>
                  )}
                </div>

                {/* Branch - show if consultationType is NOT Home Visit or Virtual Consultation */}
                {!["Home Visit", "Virtual Consultation"].includes(
                  form.consultationType
                ) && (
                  <div>
                    <label className="block font-medium mb-1 text-[#204080] text-sm">
                      Select Branch <span className="text-red-600">*</span>
                    </label>
                    <select
                      name="branch"
                      value={form.branch}
                      onChange={(e) => {
  setForm({ ...form, branch: e.target.value });
  setErrors({ ...errors, branch: "" }); // clear error as user types
}}

                      className="w-full border rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#204080] bg-white text-black"
                    >
                      <option value="">Select a branch</option>
                      <option value="Gajuwaka Branch">Gajuwaka Branch</option>
                      <option value="Kurmannapalem Branch">
                        Kurmannapalem Branch
                      </option>
                    </select>
                    {errors.branch && (
  <p className="text-red-600 text-sm mt-1">{errors.branch}</p>
)}

                  </div>
                )}

                {/* Date */}
                <div>
                  <label className="block font-medium mb-1 text-[#204080] text-sm">
                    Date <span className="text-red-600">*</span>
                  </label>
                  <DatePicker
                    selected={form.date ? new Date(form.date) : null}
                    onChange={(date) => {
  setForm({
    ...form,
    date: date ? format(date, "yyyy-MM-dd") : "",
  });
  setErrors({ ...errors, date: "" }); // clear error as user selects
}}
teFormat="dd/MM/yyyy"
                    minDate={minDateObj}
                    maxDate={maxDateObj}
                    placeholderText="Select a Date"
                    wrapperClassName="w-full"
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
                  />
                  {errors.date && (
  <p className="text-red-600 text-sm mt-1">{errors.date}</p>
)}

                </div>

                <div>
                  <label className="block font-medium mb-1 text-[#204080] text-sm">
                    Time <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={form.time}
                    onChange={(e) => {
  setForm({ ...form, time: e.target.value });
  setErrors({ ...errors, time: "" }); // clear error as user selects
}}

                    className="w-full p-3 border rounded-lg"
                  >
                    <option value="">Select a time</option>
                    {availableSlots.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {errors.time && (
  <p className="text-red-600 text-sm mt-1">{errors.time}</p>
)}

                </div>

                <div>
                  <label className="block font-medium mb-1 text-[#204080] text-sm">
                    Message
                  </label>
                  <textarea
                    rows="3"
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className="w-full p-3 border rounded-lg"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    submitting
                      ? "bg-gray-300 cursor-not-allowed text-gray-600"
                      : "bg-[#204080] text-white hover:bg-blue-900"
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    "Confirm Appointment"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
